import { useState } from "react";
import { router, usePage } from "@inertiajs/react";
import type { SharedProps } from "@/types";
import clsx from "clsx";
import Panel from "@/components/ui/Panel";
import Button from "@/components/ui/Button";
import Badge from "@/components/ui/Badge";
import { Input, Checkbox } from "@/components/ui/Field";

export type StepKind =
  | "confirmation"
  | "text"
  | "number"
  | "url"
  | "watch"
  | "read"
  | "venue";

export type ChecklistItem = {
  id: number;
  title: string;
  description: string | null;
  kind: StepKind;
  required: boolean;
  due_on: string | null;
  overdue: boolean;
  completed: boolean;
  value: string | null;
  resource_url: string | null;
  resource_label: string | null;
  auto_done?: boolean;
};

export type ChecklistProgress = {
  done: number;
  total: number;
  complete: boolean;
};

export type ChecklistStage = {
  id: number | null;
  name: string;
  description: string | null;
  locked: boolean;
  progress: ChecklistProgress;
  steps: ChecklistItem[];
};

// Steps that are just a tick - nothing to type.
const TICK_KINDS: StepKind[] = ["confirmation", "watch", "read"];

function ChecklistRow({
  item,
  eventId,
  locked,
}: {
  item: ChecklistItem;
  eventId: number;
  locked: boolean;
}) {
  const [value, setValue] = useState(item.value ?? "");

  function submit(done: boolean) {
    router.post(
      `/organize/${eventId}/requirements/${item.id}`,
      { done, value },
      { preserveScroll: true },
    );
  }

  // While the handbooks are on hold, a "read it" link would land on the holding
  // page. The step still ticks -- it just says why there's nothing to open.
  const guidesLocked = usePage<SharedProps>().props.program.guides_locked;
  const heldGuide =
    guidesLocked && (item.resource_url?.startsWith("/guides") ?? false);

  const isTick = TICK_KINDS.includes(item.kind);
  // A venue step is satisfied by the event's own details, so there's nothing to
  // tick here - the form below the checklist is what completes it.
  const isAuto = item.kind === "venue";
  const disabled = locked || isAuto;

  return (
    <div className="border-line/20 flex flex-col gap-2 border-b py-3 last:border-b-0">
      <div className="flex items-start gap-3">
        {isTick && !disabled ? (
          <Checkbox
            checked={item.completed}
            onChange={(e) => submit(e.target.checked)}
            className="mt-0.5"
            aria-label={item.title}
          />
        ) : (
          <span
            className={clsx(
              "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border-2 text-[11px] font-bold",
              item.completed
                ? "border-line bg-leaf text-white"
                : "border-line/40 text-ink-soft",
            )}
            aria-hidden
          >
            {item.completed ? "✓" : ""}
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={clsx(
                "text-body font-bold",
                item.completed ? "text-ink-soft line-through" : "text-ink",
              )}
            >
              {item.title}
            </span>
            {!item.required && <Badge tone="neutral">Optional</Badge>}
            {!item.completed && item.overdue && (
              <Badge tone="danger">Overdue</Badge>
            )}
            {isAuto && item.completed && (
              <Badge tone="success">From your event details</Badge>
            )}
          </div>

          {item.description && (
            <p className="text-caption text-ink-muted mt-0.5">
              {item.description}
            </p>
          )}

          {heldGuide && (
            <p className="text-caption text-ink-soft mt-1">
              Still being written - we'll post it in your channel the moment
              it's up.
            </p>
          )}

          {/* The thing they actually have to go watch or read. */}
          {item.resource_url && !heldGuide && (
            <a
              href={item.resource_url}
              target={
                item.resource_url.startsWith("http") ? "_blank" : undefined
              }
              rel="noopener noreferrer"
              className="text-caption text-link mt-1 inline-block font-bold underline"
            >
              {item.resource_label ??
                (item.kind === "watch" ? "Watch it" : "Read it")}{" "}
              →
            </a>
          )}

          {isAuto && !item.completed && (
            <p className="text-caption text-ink-soft mt-0.5">
              Fill in the venue and date below and this ticks itself.
            </p>
          )}

          {item.due_on && !item.completed && (
            <p className="text-caption text-ink-soft mt-0.5">
              Due {item.due_on}
            </p>
          )}
        </div>
      </div>

      {!isTick && !isAuto && (
        <div className="flex flex-wrap items-center gap-2 pl-8">
          <Input
            type={item.kind === "number" ? "number" : "text"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={locked}
            placeholder={
              item.kind === "number"
                ? "0"
                : item.kind === "url"
                  ? "https://…"
                  : "Your answer"
            }
            className="max-w-xs"
          />
          <Button size="sm" onClick={() => submit(true)} disabled={locked}>
            {item.completed ? "Update" : "Submit"}
          </Button>
          {item.completed && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => submit(false)}
              disabled={locked}
            >
              Clear
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function Stage({
  stage,
  index,
  eventId,
}: {
  stage: ChecklistStage;
  index: number;
  eventId: number;
}) {
  return (
    <Panel
      title={`${index + 1}. ${stage.name.toLowerCase().replace(/\s+/g, "-")}`}
      actions={
        stage.locked ? (
          <Badge tone="neutral">Locked</Badge>
        ) : stage.progress.complete ? (
          <Badge tone="success">Done</Badge>
        ) : (
          <Badge tone="warning">
            {stage.progress.done}/{stage.progress.total}
          </Badge>
        )
      }
    >
      <div className="flex flex-col">
        <div className="flex flex-col gap-1 pb-2">
          <h3 className="font-display text-subheading text-ink font-bold">
            {stage.name}
          </h3>
          {stage.description && (
            <p className="text-caption text-ink-muted">{stage.description}</p>
          )}
          {/* Locked stages stay visible on purpose - seeing what's coming is
              the point of splitting the work into stages. */}
          {stage.locked && (
            <p className="text-caption text-ink-soft">
              Opens once you've finished the steps above.
            </p>
          )}
        </div>

        <div className={clsx("flex flex-col", stage.locked && "opacity-50")}>
          {stage.steps.map((item) => (
            <ChecklistRow
              key={item.id}
              item={item}
              eventId={eventId}
              locked={stage.locked}
            />
          ))}
        </div>
      </div>
    </Panel>
  );
}

// The first stage that only makes sense once people can sign up ("Get RSVPs
// in"). Everything from here on is about filling and then running the room.
const RSVP_STAGE_INDEX = 2;

export default function EventChecklist({
  stages,
  eventId,
}: {
  stages: ChecklistStage[];
  eventId: number;
}) {
  const rsvpsOpen = usePage<SharedProps>().props.program.rsvps_open;

  if (!stages || stages.length === 0) return null;

  // Sign-ups open well after events are approved, so the later stages are full
  // of steps nobody can act on yet. A wall of locked boxes reads as something
  // being broken, so they collapse into one "soon" panel that still says what's
  // coming.
  const open = rsvpsOpen ? stages : stages.slice(0, RSVP_STAGE_INDEX);
  const soon = rsvpsOpen ? [] : stages.slice(RSVP_STAGE_INDEX);

  return (
    <div className="flex flex-col gap-5">
      {open.map((stage, i) => (
        <Stage
          key={stage.id ?? `ungrouped-${i}`}
          stage={stage}
          index={i}
          eventId={eventId}
        />
      ))}

      {soon.length > 0 && (
        <Panel
          title="coming-soon.txt"
          actions={<Badge tone="neutral">Soon</Badge>}
        >
          <div className="flex flex-col gap-2">
            <h3 className="font-display text-subheading text-ink font-bold">
              The rest unlocks when sign-ups open
            </h3>
            <p className="text-caption text-ink-muted">
              Filling the room, the week-of checklist and check-in all arrive
              once we open RSVPs. Nothing here needs you yet - get your venue
              locked in the meantime.
            </p>
            <ul className="text-caption text-ink-soft flex flex-col gap-1 pt-1">
              {soon.map((stage, i) => (
                <li key={stage.id ?? `soon-${i}`}>
                  {RSVP_STAGE_INDEX + i + 1}. {stage.name}
                </li>
              ))}
            </ul>
          </div>
        </Panel>
      )}
    </div>
  );
}
